import { useState } from "react";
import { useFormContext } from "../../../../context/houseOwnerSignupFormContext";
import Resizer from "react-image-file-resizer";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../../../../components/ui/avatar";
import { FaUser, FaPhone, FaEdit } from "react-icons/fa";
import { StepFooterNav } from "../../shared/NavigationFooter";

export function Step1({ nextStep, preStep, currentStep, totalSteps }) {
  const { formData, handleChange, handleImageChange } = useFormContext();
  const [selectedImage, setSelectedImage] = useState(formData.image);
  const [errors, setErrors] = useState({});

  const handleImageFileChange = (event) => {
    const file = event.target.files[0];
    const validImageTypes = ["image/jpeg", "image/png"];

    if (file && validImageTypes.includes(file.type)) {
      Resizer.imageFileResizer(
        file,
        81,
        77,
        file.type === "image/jpeg" ? "JPEG" : "PNG",
        100,
        0,
        (uri) => {
          setSelectedImage(uri);
          handleImageChange(uri);
        },
        "base64"
      );
    } else {
      setSelectedImage(null);
      handleImageChange(null);
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.userName) newErrors.userName = "User name is required";
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    return newErrors;
  };

  const handleNext = () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) setErrors(formErrors);
    else {
      setErrors({});
      nextStep();
    }
  };

  return (
    <div className="flex flex-col px-6 py-10">
      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900 text-center">Sign Up</h2>
      <p className="text-gray-600 mb-8 text-center">Fill in your personal details to continue</p>

      {/* Profile Image (fully clickable) */}
      <div className="flex justify-center mb-8 relative">
        <label htmlFor="profileImageInput" className="cursor-pointer relative">
          <Avatar className="w-24 h-24 relative">
            <AvatarImage src={selectedImage} />
            <AvatarFallback>
              <FaUser className="w-10 h-10 text-gray-500" />
            </AvatarFallback>

            {/* Edit icon overlay */}
            <div className="absolute bottom-2 right-2 bg-white rounded-full p-1 shadow-md">
              <FaEdit className="w-6 h-6 text-gray-700" />
            </div>
          </Avatar>
        </label>
        <input
          id="profileImageInput"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageFileChange}
        />
      </div>

      {/* Form Fields with Labels + Icons */}
      <div className="space-y-6">
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaUser className="w-4 h-4" /> First Name
          </Label>
          <Input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter your first name"
          />
          {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaUser className="w-4 h-4" /> Last Name
          </Label>
          <Input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter your last name"
          />
          {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaUser className="w-4 h-4" /> Username
          </Label>
          <Input
            type="text"
            name="userName"
            value={formData.userName}
            onChange={handleChange}
            placeholder="Choose a username"
          />
          {errors.userName && <p className="text-xs text-red-500 mt-1">{errors.userName}</p>}
        </div>

        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaPhone className="w-4 h-4" /> Phone Number <span className="text-sm opacity-70">(Canadians only)</span>
          </Label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="1234567890"
          />
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
        </div>

        <div>
          <Label className="mb-2">Referral Code (optional)</Label>
          <Input
            type="text"
            name="referredBy"
            value={formData.referredBy}
            onChange={handleChange}
            placeholder="e.g., 13223"
          />
        </div>
      </div>

      {/* Step Footer Navigation */}
      <StepFooterNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={preStep}
        onNext={handleNext}
        nextDisabled={false}
      />
    </div>
  );
}
